import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowBack, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { practiceUsersApi } from "apis";
import AccountStatusSection from "components/features/profile/AccountStatusSection";
import BranchesSection from "components/features/profile/BranchesSection";
import PracticeInformationSection from "components/features/profile/PracticeInformationSection";
import UnsavedChangesModal from "components/features/profile/UnsavedChangesModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import { FirebaseStorageService } from "helpers/firebaseStorage";
import { useEffect, useRef, useState, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import {
  userProfileSchema,
  UserProfileFormData,
  providerSpecialtyOptions,
  ProviderSpecialtyType,
} from "validations/userSchemas";

function PracticeDetailPage() {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const initialFormDataRef = useRef<UserProfileFormData | null>(null);

  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState<
    string | null
  >();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["practice-profile", practiceId],
    queryFn: async () => {
      return await practiceUsersApi.getPracticeProfileByUserId({
        userId: practiceId || "",
      });
    },
    enabled: Boolean(practiceId),
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: UserProfileFormData) => {
      return await practiceUsersApi.updatePracticeUser({
        userId: practiceId || "",
        practiceProfileUpdateDto: {
          firstName: formData.firstName,
          middleName: formData.middleName,
          lastName: formData.lastName,
          npi: formData.npi ?? "",
          phoneNumber: formData.phoneNumber ?? "",
          profileImage: formData.profileImage as string,
          specialty: formData.specialties as string[],
          ...(formData.isActive !== undefined && {
            isActive: formData.isActive,
          }),
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["practice-profile", practiceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["practiceUsers"],
      });
      showSuccess(
        "Profile Updated",
        "Staff profile has been updated successfully."
      );
    },
    onError: (error) => {
      console.error("API Error updating practice user:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update profile. Please try again.";
      showError("Update Failed", errorMessage);
    },
  });

  const userProfileForm = useForm({
    resolver: yupResolver(userProfileSchema),
    defaultValues: {
      id: "",
      email: "",
      profileImage: null,
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      npi: "",
      specialties: [],
      isActive: undefined,
    },
    mode: "onChange",
  });

  // Function to compare form data and detect changes
  const checkForChanges = useCallback((currentData: UserProfileFormData) => {
    if (!initialFormDataRef.current) return false;

    const initial = initialFormDataRef.current;

    return (
      initial.firstName !== currentData.firstName ||
      initial.middleName !== currentData.middleName ||
      initial.lastName !== currentData.lastName ||
      initial.phoneNumber !== currentData.phoneNumber ||
      initial.npi !== currentData.npi ||
      JSON.stringify(initial.specialties) !==
        JSON.stringify(currentData.specialties) ||
      initial.profileImage !== currentData.profileImage ||
      (initial.isActive !== undefined &&
        initial.isActive !== currentData.isActive)
    );
  }, []);

  // Watch for form changes
  const watchedValues = userProfileForm.watch();

  useEffect(() => {
    if (initialFormDataRef.current) {
      const hasChanges = checkForChanges(watchedValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [watchedValues, checkForChanges]);

  useEffect(() => {
    if (data) {
      const validSpecialties = (data.specialty || []).filter(
        (spec: string): spec is ProviderSpecialtyType =>
          providerSpecialtyOptions.includes(spec as ProviderSpecialtyType)
      );

      const formData: UserProfileFormData = {
        id: data.id || "",
        email: data.email || "",
        profileImage: data.profilePicture || null,
        firstName: data.firstName || "",
        middleName: data.middleName || "",
        lastName: data.lastName || "",
        phoneNumber: data.phoneNumber || "",
        npi: data.npi || "",
        specialties: validSpecialties,
        isActive: data.isActive,
      };

      userProfileForm.reset(formData);
      initialFormDataRef.current = formData;
      setCurrentProfileImageUrl(data.profilePicture ?? null);
      setHasUnsavedChanges(false);
    } else {
      userProfileForm.reset({});
      initialFormDataRef.current = null;
      setCurrentProfileImageUrl(null);
      setHasUnsavedChanges(false);
    }
  }, [data, userProfileForm, checkForChanges]);

  // Navigation blocking
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleNavigation = useCallback(
    (path: string) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(path);
        setShowUnsavedModal(true);
      } else {
        navigate(path);
      }
    },
    [hasUnsavedChanges, navigate]
  );

  const handleSaveAndGo = useCallback(async () => {
    const currentData = userProfileForm.getValues();
    await updateMutation.mutateAsync(currentData);

    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);

    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate, updateMutation, userProfileForm]);

  const handleCancelNavigation = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  }, []);

  const onSubmit = useCallback(
    async (data: UserProfileFormData) => {
      let profileImageUrl = currentProfileImageUrl ?? "";
      // Handle profile image upload if it's a File object
      if (data.profileImage instanceof File) {
        try {
          profileImageUrl = await FirebaseStorageService.uploadProfileImage(
            data.profileImage
          );
        } catch {
          throw new Error("Failed to upload profile image. Please try again.");
        }
      }
      await updateMutation.mutateAsync({
        ...data,
        profileImage: profileImageUrl,
      });

      // Update initial form data and reset unsaved changes
      initialFormDataRef.current = data;
      setHasUnsavedChanges(false);
    },
    [currentProfileImageUrl, updateMutation]
  );

  if (isLoading) {
    return (
      <DashboardLayout title="Practice Details">
        <Box className="flex justify-center items-center h-64">
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Practice Details" onNavigate={handleNavigation}>
      <Box className="flex items-center justify-between mb-4">
        <Breadcrumbs separator="/" aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => handleNavigation(EPath.PROVIDER_DASHBOARD)}
            className="cursor-pointer"
          >
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => handleNavigation(EPath.PRACTICE_MANAGEMENT)}
            className="cursor-pointer"
          >
            Staff management
          </Link>
          <Typography color="text.primary">Practice Details</Typography>
        </Breadcrumbs>
        <Box className="flex items-center gap-1">
          <Tooltip title="Back">
            <IconButton
              size="small"
              onClick={() => handleNavigation(EPath.PRACTICE_MANAGEMENT)}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <FormProvider {...userProfileForm}>
        <form
          onSubmit={userProfileForm.handleSubmit(onSubmit)}
          className="relative"
        >
          <PracticeInformationSection
            profileImage={data?.profilePicture ?? ""}
          />
          <BranchesSection branches={data?.branches ?? []} />
          <AccountStatusSection />
          <Box
            className="flex justify-center gap-4 mt-8"
            sx={{ position: "relative", zIndex: 10 }}
          >
            <Button
              type="submit"
              variant="contained"
              className="px-6 py-2 rounded-full w-full"
              disabled={updateMutation.isPending || !hasUnsavedChanges}
              startIcon={
                updateMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              className="px-6 py-2 rounded-full w-full"
              onClick={() => {
                userProfileForm.reset();
              }}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </FormProvider>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onCancel={handleCancelNavigation}
        onSaveAndGo={handleSaveAndGo}
        isSubmitting={updateMutation.isPending}
      />
    </DashboardLayout>
  );
}

export default PracticeDetailPage;
